import React, { useState, useEffect, useRef } from 'react';
import { User, Bot } from 'lucide-react';
import './SimulatedChatDemo.css';

const SimulatedChatDemo = ({ handleSignUp }) => {
  const [messages, setMessages] = useState([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const scrollRef = useRef(null);
  const containerRef = useRef(null);

  // Chat conversation script
  const chatScript = [
    {
      type: 'user',
      content: 'Quais os melhores jogos pra apostar no Brasileirão essa rodada?',
      delay: 1000
    },
    {
      type: 'bot',
      content: 'Analisando rodada do Brasileirão...',
      typing: true,
      delay: 1500
    },
    {
      type: 'bot',
      content: (
        <div className="space-y-2">
          <span className="text-[10px] font-medium tracking-[0.2em] text-white/50 uppercase">
            Top 3 Palpites - Brasileirão Série A
          </span>
          <div className="space-y-1.5">
            {[
              { match: 'Internacional vs Bahia', bet: 'Casa', odds: '1.92', conf: '67%' },
              { match: 'Santos vs Corinthians', bet: 'Ambas marcam', odds: '1.88', conf: '71%', tag: 'Neymar fator' },
              { match: 'Palmeiras vs Mirassol', bet: 'Over 2.5', odds: '1.95', conf: '69%' }
            ].map((item, idx) => (
              <div key={idx} className="bg-black/20 border border-white/[0.02] rounded p-2 hover:border-emerald-500/20 transition-all">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="text-[11px] text-white/95">{item.match}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-white/60">{item.bet}</span>
                      {item.tag && (
                        <span className="text-[9px] text-emerald-400/70 italic">{item.tag}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[11px] text-violet-400/90">{item.odds}×</div>
                    <div className="font-mono text-[10px] text-emerald-400/80">{item.conf}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
      delay: 2000
    },
    {
      type: 'user',
      content: 'E sobre o Neymar? Vale apostar nos jogos do Santos?',
      delay: 2500
    },
    {
      type: 'bot',
      content: 'Analisando performance do Neymar...',
      typing: true,
      delay: 1000
    },
    {
      type: 'bot',
      content: (
        <div className="space-y-3">
          <div className="text-sm text-white/90">
            <span className="text-[10px] font-medium tracking-[0.2em] text-emerald-400/80 uppercase block mb-2">
              Análise Neymar - Santos vs Corinthians
            </span>
            <div className="bg-black/30 border border-white/[0.03] rounded p-3 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/70">xG Contribuição</span>
                <span className="font-mono text-emerald-400/90">1.84/jogo</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/70">Finalizações Médias</span>
                <span className="font-mono text-emerald-400/90">4.2/jogo</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/70">Melhor Palpite</span>
                <span className="font-mono text-violet-400/90">Neymar finalizar</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/70">Confiança</span>
                <span className="font-mono text-emerald-400/90">73%</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-white/70">
            Santos em casa com Neymar tem média 2.1 gols. Corinthians defesa fragilizada.
          </p>
        </div>
      ),
      delay: 2000
    },
    {
      type: 'user',
      content: 'Compara Raphael Veiga com Hulk pra essa rodada',
      delay: 2500
    },
    {
      type: 'bot',
      content: 'Comparando jogadores...',
      typing: true,
      delay: 1000
    },
    {
      type: 'bot',
      content: (
        <div className="space-y-2">
          <span className="text-[10px] font-medium tracking-[0.2em] text-white/50 uppercase">
            Veiga vs Hulk - Comparativo
          </span>
          <div className="space-y-1.5">
            <div className="bg-black/20 border border-emerald-500/10 rounded p-2">
              <div className="text-[11px] text-white/95 mb-1">Raphael Veiga - Palmeiras vs Mirassol</div>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div>
                  <span className="text-white/50 block">xG+xA</span>
                  <span className="font-mono text-white/90">1.92</span>
                </div>
                <div>
                  <span className="text-white/50 block">Forma</span>
                  <span className="font-mono text-emerald-400/90">Excelente</span>
                </div>
              </div>
              <div className="mt-1 pt-1 border-t border-white/[0.05]">
                <span className="text-[10px] text-violet-400/80">Palpite: Veiga marcar ou assistir 1.78×</span>
              </div>
            </div>

            <div className="bg-black/20 border border-violet-500/10 rounded p-2">
              <div className="text-[11px] text-white/95 mb-1">Hulk - Atlético Mineiro</div>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div>
                  <span className="text-white/50 block">xG+xA</span>
                  <span className="font-mono text-white/90">2.15</span>
                </div>
                <div>
                  <span className="text-white/50 block">Forma</span>
                  <span className="font-mono text-emerald-400/90">Artilheiro</span>
                </div>
              </div>
              <div className="mt-1 pt-1 border-t border-white/[0.05]">
                <span className="text-[10px] text-violet-400/80">Palpite: Hulk finalizar 1.65×</span>
              </div>
            </div>
          </div>
        </div>
      ),
      delay: 2000
    },
    {
      type: 'user',
      content: 'Tem algum palpite que deu green recentemente?',
      delay: 2500
    },
    {
      type: 'bot',
      content: (
        <div className="space-y-3">
          <div className="bg-black/30 border border-emerald-500/10 rounded p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-medium text-white/90">Últimos Palpites</span>
              <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400/90 rounded font-mono">
                3/3 GREEN ✅
              </span>
            </div>
            <div className="space-y-1.5 text-[10px]">
              <div className="flex justify-between items-center">
                <span className="text-white/70">Bragantino vs São Paulo - Over 2.5</span>
                <span className="text-emerald-400/90">Deu Green ✅</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/70">Fluminense vs Athletico-PR - Under 3.5</span>
                <span className="text-emerald-400/90">Deu Green ✅</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/70">Cruzeiro vs Vasco - Dupla chance (Casa/Empate)</span>
                <span className="text-emerald-400/90">Deu Green ✅</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-white/70">
            Taxa de acerto 74% em palpites de alta confiança. Análise baseada em dados reais.
          </p>
        </div>
      ),
      delay: 2000
    }
  ];

  // Auto-scroll effect
  useEffect(() => {
    if (scrollRef.current && containerRef.current) {
      const scrollHeight = scrollRef.current.scrollHeight;
      const containerHeight = containerRef.current.clientHeight;

      // Only scroll if content exceeds container
      if (scrollHeight > containerHeight) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }
  }, [messages]);

  // Message typing effect
  useEffect(() => {
    if (currentMessageIndex < chatScript.length) {
      const currentMessage = chatScript[currentMessageIndex];
      const timer = setTimeout(() => {
        if (currentMessage.typing) {
          // Show typing indicator
          setMessages(prev => [...prev, { ...currentMessage, isTyping: true }]);

          // Replace with actual message after short delay
          setTimeout(() => {
            setMessages(prev => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1] = {
                ...chatScript[currentMessageIndex + 1],
                isTyping: false
              };
              return newMessages;
            });
            setCurrentMessageIndex(currentMessageIndex + 2);
          }, 1000);
        } else {
          setMessages(prev => [...prev, currentMessage]);
          setCurrentMessageIndex(currentMessageIndex + 1);
        }
      }, currentMessage.delay);

      return () => clearTimeout(timer);
    } else {
      // Reset and loop the conversation
      const resetTimer = setTimeout(() => {
        setMessages([]);
        setCurrentMessageIndex(0);
      }, 5000);

      return () => clearTimeout(resetTimer);
    }
  }, [currentMessageIndex]);

  return (
    <div
      ref={containerRef}
      className="relative bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg overflow-hidden"
      style={{ height: '420px' }}
    >
      {/* Chat header */}
      <div className="absolute top-0 left-0 right-0 bg-black/50 backdrop-blur-sm border-b border-white/[0.05] px-4 py-3 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-500/70 rounded-full animate-pulse"></span>
            <span className="text-[11px] font-medium tracking-[0.15em] text-white/30 uppercase">
              Chat Demo
            </span>
          </div>
          <span className="text-[10px] text-white/20 font-mono">
            Auto-replay
          </span>
        </div>
      </div>

      {/* Messages container */}
      <div
        ref={scrollRef}
        className="h-full pt-14 pb-4 px-4 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10"
        style={{
          scrollBehavior: 'smooth',
          maskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)'
        }}
      >
        <div className="space-y-4 py-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}
                         animate-fadeIn`}
            >
              {message.type === 'bot' && (
                <div className="flex-shrink-0 w-7 h-7 rounded bg-gradient-to-br from-emerald-500/10 to-violet-500/10 border border-white/[0.05] flex items-center justify-center">
                  <Bot className="w-4 h-4 text-emerald-400/70" />
                </div>
              )}

              <div className={`max-w-[340px] ${message.type === 'user' ? 'order-first' : ''}`}>
                <div
                  className={`
                    ${message.type === 'user'
                      ? 'bg-gradient-to-r from-emerald-500/10 to-violet-500/10 border-emerald-500/20 text-white/95'
                      : 'bg-white/[0.02] border-white/[0.05] text-white/90'
                    }
                    border backdrop-blur-sm rounded-lg px-3 py-2 text-xs
                  `}
                >
                  {message.isTyping ? (
                    <div className="flex gap-1 py-1">
                      <span className="w-1.5 h-1.5 bg-white/30 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-white/30 rounded-full animate-bounce ml-1" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-white/30 rounded-full animate-bounce ml-1" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  ) : (
                    message.content
                  )}
                </div>
              </div>

              {message.type === 'user' && (
                <div className="flex-shrink-0 w-7 h-7 rounded bg-white/[0.05] border border-white/[0.05] flex items-center justify-center">
                  <User className="w-4 h-4 text-white/50" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Overlay - appears after first few messages */}
      {messages.length >= 4 && (
        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={handleSignUp}
            className="w-full px-6 py-3 border border-violet-500/30 text-[11px] text-white/70
                     hover:text-violet-300/80 hover:border-violet-400/50 transition-all
                     bg-black/50 backdrop-blur-sm uppercase tracking-[0.15em]"
          >
            Testar Agora
          </button>
        </div>
      )}
    </div>
  );
};

export default SimulatedChatDemo;